import {
  Badge,
  Button,
  createTableColumn,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  Option,
  type TableColumnDefinition,
  Toolbar,
  tokens,
} from "@fluentui/react-components"
import {
  AddRegular,
  GlobeRegular,
  MoreHorizontalRegular,
  SearchRegular,
} from "@fluentui/react-icons"
import type {
  CreateDomainRequest,
  Domain,
  DomainPage,
  DomainStatus,
  UpdateDomainRequest,
} from "@rema/shared/schemas/domains"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { useSession } from "../../auth/session"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { PageHeader } from "../../components/page-header"
import { api } from "../../lib/api"
import { DomainFormDrawer } from "./domain-form-drawer"
import { domainsI18n as i18n } from "./i18n"
import { SuspendDomainDialog } from "./suspend-dialog"
import { TransferMasterDrawer } from "./transfer-master-drawer"

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: {
    minWidth: "240px",
  },
  statusFilter: {
    minWidth: "160px",
  },
  spacer: {
    marginLeft: "auto",
  },
})

// Clave base de cache; los filtros activos se añaden para que cambiar de filtro
// reinicie la paginación por cursor.
const QUERY_KEY = ["admin", "domains"] as const

type StatusFilter = DomainStatus | "all"

const STATUS_LABEL: Record<DomainStatus, string> = {
  active: i18n.status.active,
  suspended: i18n.status.suspended,
}

const fetchDomains = (cursor: string | null, q: string, status: StatusFilter) =>
  api.get<DomainPage>("/admin/domains", {
    query: {
      cursor: cursor ?? undefined,
      q: q.trim() || undefined,
      status: status === "all" ? undefined : status,
    },
  })

export const DomainsPage = () => {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { hasScope } = useSession()
  // Crear dominios es operación de instancia: el gate es el scope real que exige la
  // API (POST /admin/domains ⇒ domains:write, exclusivo del instance-admin). No vale
  // is_master, porque el admin de instancia ES el maestro del dominio system.
  const canCreate = hasScope("domains:write")

  const [search, setSearch] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")

  // Estado de los paneles (alta/edición comparten drawer; transferencia y suspensión aparte).
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Domain | null>(null)
  const [transferTarget, setTransferTarget] = useState<Domain | null>(null)
  const [statusTarget, setStatusTarget] = useState<Domain | null>(null)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const createMutation = useMutation({
    mutationFn: (input: CreateDomainRequest) => api.post<Domain>("/admin/domains", input),
    onSuccess: invalidate,
  })
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDomainRequest }) =>
      api.patch<Domain>(`/admin/domains/${id}`, input),
    onSuccess: invalidate,
  })
  const transferMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.post<Domain>(`/admin/domains/${id}/transfer-master`, { user_id: userId }),
    onSuccess: invalidate,
  })
  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: string; next: DomainStatus }) =>
      api.post<Domain>(`/admin/domains/${id}/${next === "suspended" ? "suspend" : "activate"}`),
    onSuccess: invalidate,
  })

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  // Solo usa setters de estado (estables): el array de deps queda vacío sin riesgo de stale.
  const columns = useMemo<TableColumnDefinition<Domain>[]>(
    () => [
      createTableColumn<Domain>({
        columnId: "slug",
        renderHeaderCell: () => i18n.columns.slug,
        renderCell: (item) => item.slug,
      }),
      createTableColumn<Domain>({
        columnId: "name",
        renderHeaderCell: () => i18n.columns.name,
        renderCell: (item) => item.name,
      }),
      createTableColumn<Domain>({
        columnId: "master",
        renderHeaderCell: () => i18n.columns.master,
        renderCell: (item) => item.master_user_id ?? i18n.noMaster,
      }),
      createTableColumn<Domain>({
        columnId: "registration",
        renderHeaderCell: () => i18n.columns.registration,
        renderCell: (item) => (
          <Badge
            appearance="outline"
            color={item.policies.registration.enabled ? "brand" : "informative"}
          >
            {item.policies.registration.enabled ? i18n.registration.on : i18n.registration.off}
          </Badge>
        ),
      }),
      createTableColumn<Domain>({
        columnId: "status",
        renderHeaderCell: () => i18n.columns.status,
        renderCell: (item) => (
          <Badge appearance="filled" color={item.status === "active" ? "success" : "danger"}>
            {STATUS_LABEL[item.status]}
          </Badge>
        ),
      }),
      createTableColumn<Domain>({
        columnId: "actions",
        renderHeaderCell: () => "",
        renderCell: (item) => (
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                aria-label={i18n.actions.label}
                icon={<MoreHorizontalRegular />}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  onClick={() => {
                    setEditing(item)
                    setFormOpen(true)
                  }}
                >
                  {i18n.actions.edit}
                </MenuItem>
                <MenuItem onClick={() => setTransferTarget(item)}>{i18n.actions.transfer}</MenuItem>
                <MenuItem onClick={() => setStatusTarget(item)}>
                  {item.status === "active" ? i18n.actions.suspend : i18n.actions.activate}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ),
      }),
    ],
    [],
  )

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={i18n.search}>
      <Input
        className={styles.search}
        value={search}
        onChange={(_, data) => setSearch(data.value)}
        placeholder={i18n.search}
        contentBefore={<SearchRegular />}
        aria-label={i18n.search}
      />
      <Dropdown
        className={styles.statusFilter}
        value={status === "all" ? i18n.filterAll : STATUS_LABEL[status]}
        selectedOptions={[status]}
        onOptionSelect={(_, data) => setStatus((data.optionValue as StatusFilter) ?? "all")}
        aria-label={i18n.filterStatus}
      >
        <Option value="all">{i18n.filterAll}</Option>
        <Option value="active">{i18n.status.active}</Option>
        <Option value="suspended">{i18n.status.suspended}</Option>
      </Dropdown>
      {canCreate ? (
        <Button
          className={styles.spacer}
          appearance="primary"
          icon={<AddRegular />}
          onClick={openCreate}
        >
          {i18n.create}
        </Button>
      ) : null}
    </Toolbar>
  )

  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <DataGridPage<Domain>
        queryKey={[...QUERY_KEY, search.trim(), status]}
        fetchPage={(cursor) => fetchDomains(cursor, search, status)}
        columns={columns}
        getRowId={(item) => item.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<GlobeRegular />}
            title={i18n.empty.title}
            description={i18n.empty.description}
            action={
              canCreate ? (
                <Button appearance="primary" icon={<AddRegular />} onClick={openCreate}>
                  {i18n.empty.action}
                </Button>
              ) : undefined
            }
          />
        }
      />

      <DomainFormDrawer
        open={formOpen}
        domain={editing}
        onClose={() => setFormOpen(false)}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <TransferMasterDrawer
        open={transferTarget !== null}
        domain={transferTarget}
        onClose={() => setTransferTarget(null)}
        onTransfer={(id, userId) => transferMutation.mutateAsync({ id, userId })}
      />

      <SuspendDomainDialog
        domain={statusTarget}
        confirming={statusMutation.isPending}
        onCancel={() => setStatusTarget(null)}
        onConfirm={async (domain) => {
          await statusMutation.mutateAsync({
            id: domain.id,
            next: domain.status === "active" ? "suspended" : "active",
          })
          setStatusTarget(null)
        }}
      />
    </>
  )
}
